"use client";
import { useEffect, useRef, useImperativeHandle, forwardRef, useState, useCallback, useMemo } from "react";
import { useApiIsLoaded, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useMapsConfig } from "@/hooks/useMapsConfig";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";
import { createRouteMarkerContent } from "@/utils/tourRouteMapMarkers";
import { resolveGoogleMapId } from "@/lib/google-maps-constants";
import {
    interpolateSegment,
    locationsFingerprint as computeLocationsFingerprint,
    segmentStepCount,
    type LatLngLiteral,
} from "@/utils/tourRouteMapGeometry";
import { CookieGatedMap } from "@/components/cookie-consent/CookieGatedMap";
import GoogleMapsApiProvider from "@/components/maps/GoogleMapsApiProvider";
import { useCookieServiceAllowed } from "@/hooks/useCookieServiceAllowed";

type GoogleMapInstance = google.maps.Map;
type AdvancedMarker = google.maps.marker.AdvancedMarkerElement;
type GoogleInfoWindowInstance = google.maps.InfoWindow;
type GooglePolylineInstance = google.maps.Polyline;

function setAdvancedMarkerPosition(marker: AdvancedMarker, position: LatLngLiteral): void {
    marker.position = position;
}

function detachAdvancedMarker(marker: AdvancedMarker): void {
    marker.map = null;
}

/** AdvancedMarkerElement: addListener('click') deprecated → gmp-click */
function onAdvancedMarkerClick(marker: AdvancedMarker, handler: () => void): void {
    marker.addEventListener('gmp-click', handler);
}

export interface TourRouteLocation {
    location_type: 'origin' | 'waypoints' | 'destination';
    location_title: string;
    location_description?: string;
    location_address: string;
    lat?: number | null;
    lng?: number | null;
    /** Itinerary günü (scroll senkronu) */
    day_id?: string;
    day_number?: number;
    /** 1-based durak sırası (tur geneli) */
    order_index?: number;
    /** Aynı gün içindeki durak sırası (1-based) */
    day_stop_index?: number;
    /** Gündeki toplam durak sayısı */
    day_stop_total?: number;
    image?: string;
}

interface TourRouteMapProps {
    className?: string;
    locations: TourRouteLocation[];
    defaultCenter?: { lat: number; lng: number };
    defaultZoom?: number;
    isSidebarOpen?: boolean;
    centerToLocation?: { lat: number; lng: number } | null;
    animateCar?: boolean;
    carPosition?: { lat: number; lng: number } | null;
    /** Hikaye modu / marker tıklama ile vurgulanan durak (0-based) */
    activeMarkerIndex?: number | null;
    onMarkerClick?: (index: number, location: TourRouteLocation) => void;
    /** InfoWindow gün etiketi şablonu: {day} yer tutucu */
    dayLabelTemplate?: string;
    /** Gün içi durak etiketi: {current} ve {total} */
    dayStopLabelTemplate?: string;
}

export interface TourRouteMapRef {
    centerToLocation: (lat: number, lng: number, zoom?: number) => void;
    animateCarToLocation: (from: { lat: number; lng: number }, to: { lat: number; lng: number }) => void;
    /** Polyline üzerinde indeksler arası araba animasyonu */
    animateCarBetweenIndices: (fromIndex: number, toIndex: number) => void;
    focusMarkerIndex: (index: number, zoom?: number) => void;
    /** Kaynak lokasyon indeksiyle hizalıdır; geocode edilemeyen noktalar null kalır. */
    getResolvedCoordinates: () => Array<LatLngLiteral | null>;
}

interface RenderedMarker {
    marker: AdvancedMarker;
    location: TourRouteLocation;
    sourceIndex: number;
    displayIndex: number;
}

const TourRouteMap = forwardRef<TourRouteMapRef, TourRouteMapProps>(({
    className,
    locations,
    defaultCenter = { lat: 39.9334, lng: 32.8597 }, // Türkiye merkez
    defaultZoom = 6,
    isSidebarOpen = false,
    centerToLocation: centerToLocationProp,
    animateCar: animateCarProp,
    carPosition: carPositionProp,
    activeMarkerIndex = null,
    onMarkerClick,
    dayLabelTemplate = 'Day {day}',
    dayStopLabelTemplate = 'Stop {current}/{total}',
}, ref) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [retryKey, setRetryKey] = useState(0);
    const setLoadErrorRef = useRef(setLoadError);
    setLoadErrorRef.current = setLoadError;
    const apiIsLoaded = useApiIsLoaded();
    const mapsLib = useMapsLibrary('maps');
    const markerLib = useMapsLibrary('marker');
    const geocodingLib = useMapsLibrary('geocoding');
    const { api_key: googleMapsApiKey, map_id, isLoading: mapsConfigLoading, refetch: refetchMapsConfig } = useMapsConfig();
    const resolvedMapId = useMemo(() => resolveGoogleMapId(map_id), [map_id]);
    const mapsReady = apiIsLoaded && Boolean(mapsLib) && Boolean(markerLib) && Boolean(geocodingLib);
    const { allowed: mapsConsentAllowed } = useCookieServiceAllowed('google_maps');
    const markersRef = useRef<RenderedMarker[]>([]);
    const infoWindowRef = useRef<GoogleInfoWindowInstance | null>(null);
    const polylineRef = useRef<GooglePolylineInstance | null>(null);
    const activeSegmentPolylineRef = useRef<GooglePolylineInstance | null>(null);
    const mapInstanceRef = useRef<GoogleMapInstance | null>(null);
    const carMarkerRef = useRef<AdvancedMarker | null>(null);
    const carAnimationRef = useRef<number | null>(null);
    const previousLocationRef = useRef<{ lat: number; lng: number } | null>(null);
    /** Harita yüklenmeden önce çağrılan centerToLocation için bekleyen merkez (hikaye modu sonraki/önceki). */
    const pendingCenterRef = useRef<{ lat: number; lng: number; zoom: number } | null>(null);
    /** Geocode dahil haritada kullanılan sıralı koordinatlar (points sırasıyla aynı). */
    const resolvedCoordinatesRef = useRef<Array<LatLngLiteral | null>>([]);
    /** Harita yalnızca bir kez init edilir (Google istek maliyetini azaltmak için). */
    const hasInitializedRef = useRef(false);
    const lastRetryKeyRef = useRef(0);
    const locationsRef = useRef(locations);
    locationsRef.current = locations;
    const defaultCenterRef = useRef(defaultCenter);
    const defaultZoomRef = useRef(defaultZoom);
    defaultCenterRef.current = defaultCenter;
    defaultZoomRef.current = defaultZoom;
    const activeMarkerIndexRef = useRef(activeMarkerIndex);
    activeMarkerIndexRef.current = activeMarkerIndex;
    const onMarkerClickRef = useRef(onMarkerClick);
    onMarkerClickRef.current = onMarkerClick;
    const dayLabelTemplateRef = useRef(dayLabelTemplate);
    dayLabelTemplateRef.current = dayLabelTemplate;
    const dayStopLabelTemplateRef = useRef(dayStopLabelTemplate);
    dayStopLabelTemplateRef.current = dayStopLabelTemplate;
    const lastLocationsFingerprintRef = useRef('');
    const locationsFingerprint = computeLocationsFingerprint(locations);
    const markerCountRef = useRef(0);

    const applyMarkerStyles = useCallback((activeIndex: number | null) => {
        const total = markerCountRef.current;
        if (total === 0) return;
        markersRef.current.forEach(({ marker, location, sourceIndex, displayIndex }) => {
            const isActive = activeIndex === sourceIndex;
            marker.content = createRouteMarkerContent({
                index: displayIndex,
                total,
                isActive,
                locationType: location.location_type,
            });
            marker.zIndex = isActive ? 500 : displayIndex === 0 || displayIndex === total - 1 ? 200 : 100;
        });
    }, []);

    const applyRouteHighlight = useCallback((activeIndex: number | null) => {
        const coords = resolvedCoordinatesRef.current;
        const routeCoords = coords.filter((coord): coord is LatLngLiteral => coord != null);
        if (!mapsLib || routeCoords.length < 2) return;

        if (polylineRef.current) {
            (polylineRef.current as { setOptions?: (o: Record<string, unknown>) => void }).setOptions?.({
                strokeOpacity: activeIndex != null ? 0.3 : 0.6,
                strokeWeight: activeIndex != null ? 2 : 3,
            });
        }

        if (activeSegmentPolylineRef.current) {
            activeSegmentPolylineRef.current.setMap(null);
            activeSegmentPolylineRef.current = null;
        }

        if (activeIndex == null) return;
        const activeCoordinate = coords[activeIndex];
        const nextCoordinate = coords.slice(activeIndex + 1).find((coord): coord is LatLngLiteral => coord != null);
        if (!activeCoordinate || !nextCoordinate) return;

        const segmentPath = [activeCoordinate, nextCoordinate];
        activeSegmentPolylineRef.current = new mapsLib.Polyline({
            path: segmentPath,
            map: mapInstanceRef.current as GoogleMapInstance,
            strokeColor: '#E31E24',
            strokeWeight: 5,
            strokeOpacity: 1,
        });
    }, [mapsLib]);

    const runCarAlongPath = useCallback((path: LatLngLiteral[]) => {
        if (!mapInstanceRef.current || !markerLib || path.length < 2) return;

        const map = mapInstanceRef.current;

        if (!carMarkerRef.current) {
            const img = document.createElement('img');
            img.src = 'https://maps.google.com/mapfiles/kml/shapes/cabs.png';
            img.width = 32;
            img.height = 32;
            img.alt = '';
            carMarkerRef.current = new markerLib.AdvancedMarkerElement({
                position: path[0],
                map: map as GoogleMapInstance,
                content: img,
                zIndex: 1000,
            });
        } else {
            setAdvancedMarkerPosition(carMarkerRef.current, path[0]);
        }

        if (carAnimationRef.current) {
            cancelAnimationFrame(carAnimationRef.current);
        }

        let frame = 0;
        const animate = () => {
            if (!carMarkerRef.current) return;
            if (frame >= path.length) {
                setAdvancedMarkerPosition(carMarkerRef.current, path[path.length - 1]);
                return;
            }
            setAdvancedMarkerPosition(carMarkerRef.current, path[frame]);
            frame += 1;
            carAnimationRef.current = requestAnimationFrame(animate);
        };
        animate();
    }, [markerLib]);

    // Adres'ten koordinat çözümleme
    const geocodeAddress = (address: string): Promise<LatLngLiteral | null> => {
        return new Promise((resolve) => {
            if (!geocodingLib) {
                resolve(null);
                return;
            }

            const geocoder = new geocodingLib.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results && results.length > 0) {
                    const location = results[0].geometry.location;
                    resolve({ lat: location.lat(), lng: location.lng() });
                } else {
                    resolve(null);
                }
            });
        });
    };

    useEffect(() => {
        if (!mapsConsentAllowed) return;
        if (!mapRef.current) return;
        if (!mapsReady || !mapsLib || !markerLib || !geocodingLib) return;
        if (mapsConfigLoading || !googleMapsApiKey) {
            if (!mapsConfigLoading && !googleMapsApiKey) {
                setLoadErrorRef.current('Harita için API anahtarı tanımlanmamış. Entegrasyon Yönetimi > Harita Servisleri veya sistem ayarlarından Google Maps API key girin.');
            }
            return;
        }

        if (retryKey !== lastRetryKeyRef.current) {
            lastRetryKeyRef.current = retryKey;
            hasInitializedRef.current = false;
        }
        if (lastLocationsFingerprintRef.current !== locationsFingerprint) {
            hasInitializedRef.current = false;
            lastLocationsFingerprintRef.current = locationsFingerprint;
        }
        if (hasInitializedRef.current) return;

        setLoadError(null);
        if (mapRef.current) mapRef.current.innerHTML = '';

        const currentLocations = locationsRef.current;

        const renderMap = async () => {
            try {
            // Önce tüm adresleri geocode et
            const locationsWithCoords: Array<TourRouteLocation & { coordinates: LatLngLiteral | null; sourceIndex: number }> = [];
            
            for (const [sourceIndex, location] of currentLocations.entries()) {
                let coordinates: LatLngLiteral | null = null;
                
                // Eğer lat/lng varsa direkt kullan
                const lat = Number(location.lat);
                const lng = Number(location.lng);
                if (
                    location.lat !== null && location.lng !== null &&
                    location.lat !== undefined && location.lng !== undefined &&
                    Number.isFinite(lat) && Number.isFinite(lng) &&
                    Math.abs(lat) <= 90 && Math.abs(lng) <= 180
                ) {
                    coordinates = { lat, lng };
                } else if (location.location_address) {
                    // Adres'ten geocode et
                    coordinates = await geocodeAddress(location.location_address);
                }
                
                locationsWithCoords.push({
                    ...location,
                    coordinates,
                    sourceIndex,
                });
            }

            // Koordinatları olan lokasyonları filtrele
            const validLocations = locationsWithCoords.filter(loc => loc.coordinates !== null);
            
            if (validLocations.length === 0) {
                setLoadErrorRef.current('Gösterilecek konum bulunamadı.');
                return;
            }

            // Harita merkezini belirle (ilk lokasyon veya tüm lokasyonların ortası)
            const defCenter = defaultCenterRef.current;
            const defZoom = defaultZoomRef.current;
            let center: LatLngLiteral = defCenter;
            if (validLocations.length > 0) {
                const firstLocation = validLocations[0];
                if (firstLocation.coordinates) {
                    center = firstLocation.coordinates;
                }
            }

            // Tüm lokasyonların ortasını hesapla
            if (validLocations.length > 1) {
                const avgLat = validLocations.reduce((sum, loc) => sum + (loc.coordinates?.lat || 0), 0) / validLocations.length;
                const avgLng = validLocations.reduce((sum, loc) => sum + (loc.coordinates?.lng || 0), 0) / validLocations.length;
                center = { lat: avgLat, lng: avgLng };
            }

            // Haritayı oluştur
            if (!mapRef.current) return;
            const map = new mapsLib.Map(mapRef.current, {
                center,
                zoom: validLocations.length > 1 ? 7 : defZoom,
                mapId: resolvedMapId,
            });
            mapInstanceRef.current = map;

            // Önceki marker'ları temizle
            markersRef.current.forEach(({ marker }) => detachAdvancedMarker(marker));
            markersRef.current = [];
            if (polylineRef.current) {
                polylineRef.current.setMap(null);
            }
            if (activeSegmentPolylineRef.current) {
                activeSegmentPolylineRef.current.setMap(null);
                activeSegmentPolylineRef.current = null;
            }

            // InfoWindow oluştur
            const infoWindow = new mapsLib.InfoWindow({ content: '' });
            infoWindowRef.current = infoWindow;

            // Marker'ları oluştur
            const markerPositions: LatLngLiteral[] = [];
            const totalMarkers = validLocations.length;
            markerCountRef.current = totalMarkers;
            resolvedCoordinatesRef.current = currentLocations.map(() => null);

            validLocations.forEach((location, displayIndex) => {
                if (!location.coordinates) return;

                const { sourceIndex } = location;
                const isActive = activeMarkerIndexRef.current === sourceIndex;
                const marker = new markerLib.AdvancedMarkerElement({
                    position: location.coordinates,
                    map: map,
                    title: location.location_title,
                    content: createRouteMarkerContent({
                        index: displayIndex,
                        total: totalMarkers,
                        isActive,
                        locationType: location.location_type,
                    }),
                    zIndex: isActive ? 500 : displayIndex === 0 || displayIndex === totalMarkers - 1 ? 200 : 100,
                });

                markerPositions.push(location.coordinates);
                resolvedCoordinatesRef.current[sourceIndex] = location.coordinates;

                const escapeHtml = (text: string): string => {
                    const div = document.createElement('div');
                    div.textContent = text;
                    return div.innerHTML;
                };

                const descriptionHtml = location.location_description
                    ? sanitizeCmsHtml(location.location_description)
                    : '';

                const dayBadge = location.day_number != null
                    ? `<span style="display:inline-block;margin-bottom:8px;padding:2px 10px;background:#E31E24;color:#fff;font-size:11px;font-weight:600;border-radius:999px;">${escapeHtml(dayLabelTemplateRef.current.replace('{day}', String(location.day_number)))}</span>`
                    : '';

                const stopSubLabel =
                    location.day_stop_index != null &&
                    location.day_stop_total != null &&
                    location.day_stop_total > 1
                        ? dayStopLabelTemplateRef.current
                            .replace('{current}', String(location.day_stop_index))
                            .replace('{total}', String(location.day_stop_total))
                        : '';

                const stopBadge = location.order_index != null
                    ? `<span style="display:inline-block;margin-left:6px;margin-bottom:8px;padding:2px 8px;background:#f3f4f6;color:#374151;font-size:11px;font-weight:600;border-radius:999px;">${escapeHtml(stopSubLabel || `#${location.order_index}`)}</span>`
                    : '';

                const content = `
                    <div style="padding: 16px; font-family: 'Inter', Arial, sans-serif; max-width: 280px;">
                        ${dayBadge}${stopBadge}
                        <h3 style="margin: 0 0 8px 0; color: #1a1a1a; font-size: 16px; font-weight: 600; border-bottom: 2px solid #e31e24; padding-bottom: 6px;">
                            ${escapeHtml(location.location_title || '')}
                        </h3>
                        ${descriptionHtml ? `<div style="margin: 0 0 8px 0; color: #4f545a; font-size: 14px; line-height: 1.4;">${descriptionHtml}</div>` : ''}
                        <p style="margin: 0; color: #666; font-size: 12px; line-height: 1.3;">${escapeHtml(location.location_address || '')}</p>
                    </div>
                `;

                onAdvancedMarkerClick(marker, () => {
                    infoWindow.close();
                    infoWindow.setContent(content);
                    infoWindow.open({ map, anchor: marker });
                    onMarkerClickRef.current?.(sourceIndex, currentLocations[sourceIndex]);
                });

                markersRef.current.push({ marker, location, sourceIndex, displayIndex });
            });

            // Rota çizgisi (polyline) - sıralı lokasyonlar arasında
            if (markerPositions.length > 1) {
                const polyline = new mapsLib.Polyline({
                    path: markerPositions,
                    map: map,
                    strokeColor: '#E31E24',
                    strokeWeight: 3,
                    strokeOpacity: 0.6,
                });
                polylineRef.current = polyline;

                // Tüm marker'ları görünür yapmak için bounds hesapla
                const bounds = new google.maps.LatLngBounds();
                markerPositions.forEach(pos => bounds.extend(pos));
                map.fitBounds(bounds);
            }

            // Hikaye modu harita hazır olmadan merkez istediyse uygula
            const pending = pendingCenterRef.current;
            if (pending) {
                pendingCenterRef.current = null;
                map.panTo({ lat: pending.lat, lng: pending.lng });
                if (pending.zoom != null) setTimeout(() => map.setZoom(pending.zoom!), 300);
            }

            hasInitializedRef.current = true;
            applyMarkerStyles(activeMarkerIndexRef.current ?? null);
            applyRouteHighlight(activeMarkerIndexRef.current ?? null);
            } catch (error) {
                console.error("Google Maps render hatası:", error);
                setLoadErrorRef.current('Harita yüklenemedi. Lütfen daha sonra tekrar deneyin.');
            }
        };

        void renderMap();
    }, [retryKey, googleMapsApiKey, mapsConfigLoading, locationsFingerprint, applyMarkerStyles, applyRouteHighlight, mapsConsentAllowed, mapsReady, mapsLib, markerLib, geocodingLib, resolvedMapId]);

    useEffect(() => {
        if (!hasInitializedRef.current) return;
        applyMarkerStyles(activeMarkerIndex ?? null);
        applyRouteHighlight(activeMarkerIndex ?? null);
    }, [activeMarkerIndex, applyMarkerStyles, applyRouteHighlight]);

    // Ref expose et
    useImperativeHandle(ref, () => ({
        centerToLocation: (lat: number, lng: number, zoom?: number) => {
            if (mapInstanceRef.current) {
                const map = mapInstanceRef.current;
                pendingCenterRef.current = null;
                map.panTo({ lat, lng });
                if (zoom != null) setTimeout(() => map.setZoom(zoom), 300);
            } else {
                pendingCenterRef.current = { lat, lng, zoom: zoom ?? 12 };
            }
        },
        getResolvedCoordinates: () => resolvedCoordinatesRef.current ?? [],
        focusMarkerIndex: (index: number, zoom = 11) => {
            const coords = resolvedCoordinatesRef.current[index];
            if (!coords) return;
            if (mapInstanceRef.current) {
                const map = mapInstanceRef.current;
                map.panTo(coords);
                setTimeout(() => map.setZoom(zoom), 200);
            }
        },
        animateCarBetweenIndices: (fromIndex: number, toIndex: number) => {
            const coords = resolvedCoordinatesRef.current;
            const from = coords[fromIndex];
            const to = coords[toIndex];
            if (!from || !to) return;
            const steps = segmentStepCount(from, to);
            const path = interpolateSegment(from, to, steps);
            runCarAlongPath(path);
        },
        animateCarToLocation: (from: { lat: number; lng: number }, to: { lat: number; lng: number }) => {
            const steps = segmentStepCount(from, to);
            runCarAlongPath(interpolateSegment(from, to, steps));
        },
    }), [runCarAlongPath]);

    // centerToLocation prop değiştiğinde haritayı center et
    useEffect(() => {
        if (centerToLocationProp && mapInstanceRef.current) {
            const map = mapInstanceRef.current;
            map.setCenter(centerToLocationProp);
            map.setZoom(10);
        }
    }, [centerToLocationProp]);

    // Araba animasyonu - carPositionProp değiştiğinde
    useEffect(() => {
        if (carPositionProp && previousLocationRef.current && mapInstanceRef.current && markerLib) {
            const map = mapInstanceRef.current;
            
            // Araba marker'ı oluştur veya güncelle (Google Maps KML araba ikonu)
            if (!carMarkerRef.current) {
                const img = document.createElement('img');
                img.src = 'https://maps.google.com/mapfiles/kml/shapes/cabs.png';
                img.width = 32;
                img.height = 32;
                img.alt = '';
                carMarkerRef.current = new markerLib.AdvancedMarkerElement({
                    position: previousLocationRef.current,
                    map: map,
                    content: img,
                    zIndex: 1000,
                });
            }

            // Animasyon
            const steps = 50;
            let step = 0;
            const from = previousLocationRef.current;

            if (carAnimationRef.current) {
                cancelAnimationFrame(carAnimationRef.current);
            }

            const animate = () => {
                if (step >= steps || !carMarkerRef.current) {
                    if (carMarkerRef.current) {
                        setAdvancedMarkerPosition(carMarkerRef.current, carPositionProp);
                    }
                    previousLocationRef.current = carPositionProp;
                    return;
                }

                const progress = step / steps;
                const lat = from.lat + (carPositionProp.lat - from.lat) * progress;
                const lng = from.lng + (carPositionProp.lng - from.lng) * progress;

                if (carMarkerRef.current) {
                    setAdvancedMarkerPosition(carMarkerRef.current, { lat, lng });
                }

                step++;
                carAnimationRef.current = requestAnimationFrame(animate);
            };

            animate();
        } else if (carPositionProp && !previousLocationRef.current) {
            // İlk konum - animasyon yok, direkt yerleştir
            previousLocationRef.current = carPositionProp;
        }
    }, [carPositionProp, markerLib]);

    // Mobilde hikâye paneli bottom-sheet olarak açılır; harita tam genişlikte kalmalı.
    // Masaüstünde sağ panel için yalnızca bir kez 24rem alan bırakılır.
    const containerClassName = `${className} ${isSidebarOpen ? 'md:w-[calc(100%-24rem)] transition-all duration-300' : ''}`.trim();

    if (loadError) {
        return (
            <CookieGatedMap className={containerClassName} minHeight="200px">
                <div className={containerClassName}>
                    <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-6 bg-gray-50 rounded-lg">
                        <p className="text-center text-gray-600 mb-4">{loadError}</p>
                        <button
                            type="button"
                            onClick={async () => {
                                setLoadError(null);
                                await refetchMapsConfig();
                                setRetryKey((k) => k + 1);
                            }}
                            className="px-4 py-2 bg-alsero-first text-white rounded-lg hover:bg-alsero-first/90 transition-colors font-medium"
                        >
                            Tekrar dene
                        </button>
                    </div>
                </div>
            </CookieGatedMap>
        );
    }

    return (
        <CookieGatedMap className={containerClassName} minHeight="320px">
            <div 
                ref={mapRef} 
                className={containerClassName}
            />
        </CookieGatedMap>
    );
});

TourRouteMap.displayName = 'TourRouteMap';

const TourRouteMapWithMapsApi = forwardRef<TourRouteMapRef, TourRouteMapProps>((props, ref) => (
  <GoogleMapsApiProvider>
    <TourRouteMap ref={ref} {...props} />
  </GoogleMapsApiProvider>
));

TourRouteMapWithMapsApi.displayName = 'TourRouteMapWithMapsApi';

export default TourRouteMapWithMapsApi;
